/*
 * assets/product-pdf.js
 * ---------------------------------------------------------------------
 * Generates a downloadable "Product Data Sheet" PDF from the hidden
 * template rendered by snippets/product-pdf-template.liquid.
 *
 * IMPORTANT DESIGN NOTE (read before touching the pagination logic):
 * We do NOT use html2pdf.js's own .from(el).toPdf() multi-page pipeline.
 * That pipeline renders the whole document as one tall canvas and then
 * decides page-break points itself (via its "avoid-all"/"css" modes),
 * INSERTING padding into an internal DOM clone to push content onto
 * clean page boundaries. That padding is invisible to the live DOM we
 * still hold a reference to, so any getBoundingClientRect() measurement
 * taken afterwards (to place clickable link annotations) drifts out of
 * sync with what was actually rasterized — confirmed by inspecting a
 * generated PDF's raw link annotations and finding one positioned with
 * a negative Y coordinate entirely off the page.
 *
 * Instead, each ".pdf-page" section in the template is captured with
 * html2canvas INDIVIDUALLY and added as its own PDF page directly via
 * jsPDF. There is no shared tall canvas, so nothing can ever be split
 * across a page boundary that we didn't explicitly choose, and link
 * coordinates are measured against the exact same element we just
 * captured — no drift is possible.
 *
 * html2canvas and jsPDF are loaded as their own standalone CDN scripts
 * (lazily, only on click). Earlier this loaded html2pdf.bundle.min.js
 * instead and assumed it exposed window.html2canvas / window.jspdf —
 * it doesn't; that bundle only exposes window.html2pdf as a single
 * closed-over entry point, so those globals were always undefined and
 * every generation attempt was silently failing. Loading the two
 * underlying libraries directly avoids that entirely.
 * --------------------------------------------------------------------- */

(function () {
  "use strict";

  // html2canvas-pro (not the stock html2canvas 1.4.1) — this store's
  // theme uses modern CSS color functions (oklch()) in its color-scheme
  // system, and stock html2canvas throws "unsupported color function
  // oklch" the moment it has to resolve any inherited style touching
  // one. html2canvas-pro is a maintained fork that adds support for
  // oklch/oklab/lab/lch/color-mix while keeping the same API/globals.
  var HTML2CANVAS_URL =
    "https://cdn.jsdelivr.net/npm/html2canvas-pro@1.5.8/dist/html2canvas-pro.min.js";
  var JSPDF_URL =
    "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  var QRCODE_URL =
    "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";

  var loadedScripts = {};

  function loadScript(url) {
    if (loadedScripts[url]) {
      return loadedScripts[url];
    }
    loadedScripts[url] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + url + '"]');
      if (existing) {
        existing.addEventListener("load", function () {
          resolve();
        });
        existing.addEventListener("error", function () {
          reject(new Error("Failed to load " + url));
        });
        return;
      }
      var script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error("Failed to load " + url));
      };
      document.head.appendChild(script);
    });
    return loadedScripts[url];
  }

  function ensurePdfLibs() {
    var needed = [];
    if (!window.html2canvas) needed.push(loadScript(HTML2CANVAS_URL));
    if (!((window.jspdf && window.jspdf.jsPDF) || window.jsPDF)) {
      needed.push(loadScript(JSPDF_URL));
    }
    if (!needed.length) return Promise.resolve();
    return Promise.all(needed);
  }

  function ensureQrCode() {
    if (window.QRCode) return Promise.resolve();
    return loadScript(QRCODE_URL);
  }

  // Set real `src` on every lazily-tagged image inside the hidden template
  // and wait for them all to finish loading (or fail) before rasterizing.
  function hydrateImages(root) {
    var images = Array.prototype.slice.call(
      root.querySelectorAll("img[data-pdf-src]")
    );
    var pending = images.map(function (img) {
      return new Promise(function (resolve) {
        if (!img.dataset.pdfSrc) {
          resolve();
          return;
        }
        img.crossOrigin = "anonymous";
        img.addEventListener("load", function () {
          resolve();
        });
        img.addEventListener("error", function () {
          // Missing/broken image should never block the whole PDF.
          img.remove();
          resolve();
        });
        img.src = img.dataset.pdfSrc;
        img.removeAttribute("data-pdf-src");
      });
    });
    return Promise.all(pending);
  }

  function renderQrCode(root, productUrl) {
    var target = root.querySelector("#product-pdf-qrcode");
    if (!target || !window.QRCode || !productUrl) return;
    target.innerHTML = "";
    /* eslint-disable no-new */
    new window.QRCode(target, {
      text: productUrl,
      width: 96,
      height: 96,
      correctLevel: window.QRCode.CorrectLevel.M,
    });
  }

  function setGeneratedDate(root) {
    var el = root.querySelector("[data-pdf-generated-date]");
    if (!el) return;
    var now = new Date();
    var formatted = now.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    el.textContent = formatted;
  }

  function slugForFilename(value) {
    return (value || "")
      .toString()
      .trim()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "");
  }

  function buildFilename(root) {
    var sku = slugForFilename(root.dataset.productSku) || "NOSKU";
    var name = slugForFilename(root.dataset.productTitle) || "product";
    return "PRODUCT-" + sku + "-" + name + ".pdf";
  }

  function captureDivToCanvas(div, html2canvasFn) {
    return html2canvasFn(div, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
    });
  }

  // Elements that should never be sliced in half by an automatic page
  // break. Mirrors the CSS break-inside:avoid list plus the common
  // block-level tags a rendered product description can contain.
  var SAFE_BREAK_SELECTOR =
    "p, li, h1, h2, h3, h4, h5, tr, table, ul, ol, blockquote, img, " +
    ".pdf-gallery-item, .pdf-qr-order, .pdf-header, .pdf-page1-body, " +
    ".pdf-footer, .pdf-price-tag";

  // Returns the bottom edge (in CANVAS pixel space, i.e. already scaled by
  // html2canvas's own `scale` option) of every "atomic" element inside
  // `pageDiv`. These are the only Y-coordinates we're allowed to slice a
  // multi-page canvas at — cutting anywhere else risks slicing straight
  // through the middle of a line of text or an image.
  function getSafeBreakOffsetsPx(pageDiv, pageRect, canvasScale) {
    var els = pageDiv.querySelectorAll(SAFE_BREAK_SELECTOR);
    var offsets = [];
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      var bottom = (r.bottom - pageRect.top) * canvasScale;
      if (bottom > 0) offsets.push(bottom);
    }
    offsets.sort(function (a, b) {
      return a - b;
    });
    return offsets;
  }

  // Draws `canvas` onto `pdf` as one or more full-width pages. Returns the
  // 1-based page number of the FIRST page it drew on (where any
  // in-flow content near the top of the source element — e.g. an
  // ORDER NOW button — will end up).
  //
  // When content is taller than one page, we slice the CANVAS itself —
  // but only ever at a `breakOffsets` entry (the gap just below a
  // paragraph/heading/row/image), never at a raw pixel-height boundary.
  // That's what previously produced screenshots with a line of text
  // sliced clean in half across two pages. If a single element is itself
  // taller than a full page (essentially never, for this layout) there's
  // no safe offset to use and we fall back to a hard cut rather than
  // dropping content.
  function addCanvasAsPages(pdf, canvas, pageWidthPt, pageHeightPt, breakOffsets) {
    var canvasWidthPx = canvas.width;
    var canvasHeightPx = canvas.height;
    var imgHeightPt = (canvasHeightPx / canvasWidthPx) * pageWidthPt;
    var firstPageNumber = pdf.internal.getNumberOfPages();

    if (imgHeightPt <= pageHeightPt + 0.5) {
      var imgData = canvas.toDataURL("image/jpeg", 0.95);
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidthPt, imgHeightPt);
      return firstPageNumber;
    }

    var pageHeightPx = (pageHeightPt / pageWidthPt) * canvasWidthPx;
    var offsets = breakOffsets || [];
    var cursor = 0;
    var isFirstSlice = true;
    var minSliceHeightPx = 20; // guards against a degenerate near-zero slice

    while (cursor < canvasHeightPx - 0.5) {
      var idealEnd = Math.min(cursor + pageHeightPx, canvasHeightPx);
      var actualEnd = idealEnd;

      if (idealEnd < canvasHeightPx - 0.5) {
        var best = null;
        for (var i = 0; i < offsets.length; i++) {
          var off = offsets[i];
          if (off > cursor + minSliceHeightPx && off <= idealEnd) {
            best = off;
          } else if (off > idealEnd) {
            break;
          }
        }
        if (best) actualEnd = best;
        // else: no safe break point in range (one element taller than a
        // full page) — fall back to a hard cut at idealEnd so content
        // still isn't lost.
      }

      var sliceHeightPx = actualEnd - cursor;
      if (sliceHeightPx <= 0) break;

      var sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvasWidthPx;
      sliceCanvas.height = sliceHeightPx;
      var ctx = sliceCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        cursor,
        canvasWidthPx,
        sliceHeightPx,
        0,
        0,
        canvasWidthPx,
        sliceHeightPx
      );

      if (!isFirstSlice) {
        pdf.addPage();
      }
      isFirstSlice = false;

      var sliceImgHeightPt = (sliceHeightPx / canvasWidthPx) * pageWidthPt;
      var sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
      pdf.addImage(sliceData, "JPEG", 0, 0, pageWidthPt, sliceImgHeightPt);

      cursor = actualEnd;
    }

    return firstPageNumber;
  }

  // Adds a clickable link annotation for every [data-pdf-order-link]
  // element found inside `pageDiv`, measured against `pageDiv`'s own
  // rect (captured at the same moment as the canvas), on `pageNumber`.
  function addOrderLinksForPage(pdf, pageDiv, pageRect, scale, pageNumber, productUrl) {
    var targets = pageDiv.querySelectorAll("[data-pdf-order-link]");
    if (!targets.length) return;

    pdf.setPage(pageNumber);

    targets.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var x = (rect.left - pageRect.left) * scale;
      var y = (rect.top - pageRect.top) * scale;
      var w = rect.width * scale;
      var h = rect.height * scale;

      if (!(w > 4) || !(h > 4) || isNaN(x) || isNaN(y)) return;

      // A few points of padding makes the clickable zone slightly more
      // forgiving than the visible box.
      pdf.link(Math.max(0, x - 3), Math.max(0, y - 3), w + 6, h + 6, {
        url: productUrl,
      });
    });
  }

  function buildPdf(root, productUrl) {
    var html2canvasFn = window.html2canvas;
    var JsPdfCtor = (window.jspdf && window.jspdf.jsPDF) || window.jsPDF;
    if (!html2canvasFn || !JsPdfCtor) {
      return Promise.reject(new Error("PDF libraries failed to load."));
    }

    var pageDivs = Array.prototype.slice.call(
      root.querySelectorAll(".pdf-page")
    );
    if (!pageDivs.length) {
      return Promise.reject(new Error("No page content found."));
    }

    var pdf = new JsPdfCtor({
      unit: "pt",
      format: "letter",
      orientation: "portrait",
    });
    var pageWidthPt = pdf.internal.pageSize.getWidth();
    var pageHeightPt = pdf.internal.pageSize.getHeight();

    var chain = Promise.resolve();
    var isFirstPdfPage = true;

    pageDivs.forEach(function (pageDiv) {
      chain = chain.then(function () {
        return captureDivToCanvas(pageDiv, html2canvasFn).then(function (
          canvas
        ) {
          if (!isFirstPdfPage) {
            pdf.addPage();
          }
          isFirstPdfPage = false;

          var pageRect = pageDiv.getBoundingClientRect();
          var scale = pageWidthPt / pageRect.width;
          var canvasScale = canvas.width / pageRect.width;
          var breakOffsets = getSafeBreakOffsetsPx(
            pageDiv,
            pageRect,
            canvasScale
          );

          var firstPageNumber = addCanvasAsPages(
            pdf,
            canvas,
            pageWidthPt,
            pageHeightPt,
            breakOffsets
          );

          addOrderLinksForPage(
            pdf,
            pageDiv,
            pageRect,
            scale,
            firstPageNumber,
            productUrl
          );
        });
      });
    });

    return chain.then(function () {
      return pdf;
    });
  }

  function setButtonState(button, isLoading) {
    var label = button.querySelector(".product-pdf-btn-label");
    var spinner = button.querySelector(".product-pdf-btn-spinner");
    button.disabled = isLoading;
    if (spinner) spinner.hidden = !isLoading;
    if (label) {
      label.textContent = isLoading
        ? "Preparing PDF…"
        : "Download Product Data Sheet (PDF)";
    }
  }

  function showError(wrapper, message) {
    var errorEl = wrapper.querySelector(".product-pdf-error");
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError(wrapper) {
    var errorEl = wrapper.querySelector(".product-pdf-error");
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = "";
  }

  function handleGenerate(button) {
    var wrapper = button.closest(".product-pdf-download-wrapper") || document;
    var root = document.getElementById("product-pdf-root");

    if (!root) {
      showError(wrapper, "Could not find product data to build the PDF.");
      return;
    }

    clearError(wrapper);
    setButtonState(button, true);

    var productUrl = root.dataset.productUrl || window.location.href;

    Promise.all([ensurePdfLibs(), ensureQrCode()])
      .then(function () {
        return hydrateImages(root);
      })
      .then(function () {
        setGeneratedDate(root);
        renderQrCode(root, productUrl);
        var filename = buildFilename(root);
        return buildPdf(root, productUrl).then(function (pdf) {
          pdf.save(filename);
        });
      })
      .catch(function (err) {
        console.error("[product-pdf] generation failed", err);
        showError(
          wrapper,
          "Sorry, the data sheet could not be generated. Please try again."
        );
      })
      .finally(function () {
        setButtonState(button, false);
      });
  }

  function init() {
    document.addEventListener("click", function (event) {
      var trigger = event.target.closest("[data-pdf-trigger]");
      if (!trigger) return;
      event.preventDefault();
      handleGenerate(trigger);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
